import React, { useTransition } from "react";
import {
  useRoutePathname,
  type CtaClickedValue,
  type RegisteredStatsigEvent,
  type RegisteredEventName,
  STATSIG_EVENTS,
  useStatsigClient,
} from "@repo/statsig";
import classNames from "classnames";
import { mergeDeep } from "../../helpers/merge-deep";
import type { DeepPartial } from "../../types";
import { defaultTheme } from "./theme";

/**
 * Analytics payload for a button press. `action` names the CTA and becomes the
 * `cta_clicked` event's `value`, so it's constrained to the registered
 * vocabulary in `@repo/statsig` rather than a free-form string (PE-877). Any
 * other key rides along as metadata — Statsig client metadata is
 * string-valued, so stringify numbers and booleans at the call site.
 */
export interface ActionButtonEventData {
  action: CtaClickedValue;
  [key: string]: string;
}

interface ActionButtonProps {
  onPress?: () => void;
  theme?: DeepPartial<ActionButtonTheme>;
  eventData: ActionButtonEventData;
  customEventName?: RegisteredEventName;
  className: string;
  pill?: boolean;
  children?: React.ReactNode;
  text?: string;
  form?: string;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  "data-testid"?: string;
}

export type ActionButtonTheme = {
  root: {
    base: string;
    children: string;
  };
};

// Base button styles (layout and interaction only — colors come from variant classes)
const buttonBaseStyles =
  "inline-flex justify-center font-medium text-sm focus:outline-none disabled:cursor-not-allowed";

export const BaseActionButton: React.FC<ActionButtonProps> = ({
  children,
  text,
  onPress,
  className,
  theme: customTheme = {},
  eventData,
  customEventName,
  pill = false,
  form,
  disabled,
  type = "button",
  "data-testid": dataTestId,
}) => {
  const theme = mergeDeep(defaultTheme, customTheme);
  const { logEvent } = useStatsigClient();
  const locationData = useRoutePathname();

  const handlePress = (e: React.MouseEvent<HTMLButtonElement>) => {
    // `value` is always the typed CTA name. The previous fallback chain
    // (customEventName > action > buttonText > "unknown") let an event name or
    // a rendered button label land in `value`, which broke the cta_clicked
    // enum; `action` is required and validated by the type system now.
    const standardEvent: RegisteredStatsigEvent = {
      eventName: STATSIG_EVENTS.BUTTON_PRESS,
      value: eventData.action,
      metadata: { ...locationData, ...eventData },
    };
    logEvent(standardEvent);

    // Log the custom event if provided (for backward compatibility)
    if (customEventName) {
      const customEvent: RegisteredStatsigEvent = {
        eventName: customEventName,
        metadata: { ...locationData, ...eventData },
      };
      logEvent(customEvent);
    }

    (e.target as HTMLButtonElement).blur();
    onPress?.();
  };

  // Corner radius is intentionally not set here so the consumer can pick
  // a brand-tokenized radius without fighting Tailwind class ordering.
  // Pass `pill` for backwards compatibility with any consumer that still
  // wants the previous default; otherwise the consumer's className wins.
  const buttonClasses = classNames(
    buttonBaseStyles,
    pill && "rounded-full",
    theme.root.base,
    className
  );

  if (form) {
    return (
      <button
        className={buttonClasses}
        onClick={handlePress}
        form={form}
        type={type}
        disabled={disabled}
        data-testid={dataTestId}
      >
        {text && <span className={theme.root.children}>{text}</span>}
        {!text && children}
      </button>
    );
  }

  // Use transition to prevent double clicks and indicate loading, form submit will use useActionState
  const [isPending, startTransition] = useTransition();

  return (
    <button
      className={buttonClasses}
      disabled={isPending || disabled}
      onClick={(e) => {
        startTransition(() => {
          handlePress(e);
        });
      }}
      type={type}
      data-testid={dataTestId}
    >
      {text && <span className={theme.root.children}>{text}</span>}
      {!text && children}
    </button>
  );
};
